import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Settings, UserCircle, LogOut, LayoutDashboard } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { tierLabel } from "@/lib/tiers";

/** Shared account avatar dropdown — the single source of truth used by BOTH the dashboard
 *  topbar (AppShell) and the marketing/public nav, so the two menus always mirror each other. */
export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: myProfile } = useQuery({
    queryKey: ["my-profile-slug"],
    queryFn: async () => (await api.get("/profile/me")).data,
    enabled: !!user,
  });
  if (!user) return null;

  const name = user.preferred_first_name || user.first_name || "";
  const initials = `${(user.first_name || "U")[0]}${(user.last_name || "")[0] || ""}`.toUpperCase();
  const profileSlug = myProfile?.slug;
  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full p-0.5 hover:bg-secondary" data-testid="user-menu-trigger">
          <Avatar className="h-9 w-9">
            {user.picture && <AvatarImage src={fileUrl(user.picture)} alt={name} />}
            <AvatarFallback className="bg-teal-50 text-sm text-teal-700">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          {profileSlug ? (
            <Link to={`/p/${profileSlug}`} className="block font-[600] hover:text-primary hover:underline" data-testid="menu-profile-name-link">{name} {user.last_name}</Link>
          ) : (
            <div className="font-[600]">{name} {user.last_name}</div>
          )}
          <div className="text-xs font-[400] text-muted-foreground">{user.email}</div>
          <div className="mt-1 inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-[560] text-teal-700">{tierLabel(user.tier)} plan</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/dashboard/account")} data-testid="menu-account"><Settings className="mr-2 h-4 w-4" /> Account Settings</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} data-testid="menu-edit-profile"><UserCircle className="mr-2 h-4 w-4" /> Edit Profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive focus:text-destructive"><LogOut className="mr-2 h-4 w-4" /> Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
